import React, { useState, useEffect } from 'react'
import { gql, useQuery } from '@apollo/client'
import { Row, Col, Form, Input, FormGroup, Button, Label, Alert, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap"
import Select from 'react-select'
import { BoardTypeAhead, GroupsTypeAhead, RepsTypeAhead } from "./TypeAheadComponents"
import { CreateTaskMutation } from "../Mutations"
import withApolloClient from '../../withApolloClient'
import { ForeignKeyInput } from "./ForeignKeyComponents"
import "./RenderAddCustomColumn.css"

const AllColumnsQuery = gql`query AllColumnsQuery($boardId: ID!){
    allColumns(boardId: $boardId, isArchived: false){
        edges {
            node {
                id
                pk
                title
                isRequired
                columnType
                allowedOptions
                contentType {
                    id
                    appLabel
                    model
                }
            }
        }
    }
}`

function NewTaskModal({ isOpen, closeModal, boardId: parentBoard, filterValues, ...props }) {
    const [title, setTitle] = useState("")
    const [description, setDescription] = useState("")
    const [boardId, setBoardId] = useState(parentBoard ? parentBoard : "")
    const [repIds, setRepIds] = useState([])
    const [groupId, setGroupId] = useState(null)
    const [dueDate, setDueDate] = useState("")
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(false)
    const [columnValues, setColumnValues] = useState({})

    const { data: columnsData, loading: columnsLoading, error: columnsError } = useQuery(
        AllColumnsQuery,
        {
            variables: { boardId: boardId },
            fetchPolicy: 'network-only',
            skip: !boardId,
        }
    )
    const lockedIncidentId = filterValues?.incidentId || null
    const lockedDriverId = filterValues?.driverId || null

    const setColumnValue = (columnId, value) => {
        if (value === undefined || value === null) {
            // Remove the key entirely so the column is treated as unset
            const updated = { ...columnValues }
            delete updated[columnId]
            setColumnValues(updated)
        } else {
            setColumnValues({ ...columnValues, [columnId]: value })
        }
    }

    const getColumnValue = (columnId) => {
        return columnValues[columnId] ?? ""
    }

    const isColumnEnabled = (columnNode) => {
        return columnNode.isRequired || columnValues[columnNode.id] !== undefined
    }

    const getColumnInitialValue = (columnNode) => {
        if (columnNode.columnType === "BOOLEAN") return false
        return ""
    }

    const toggleColumnEnabled = (columnNode, enabled) => {
        if (enabled) {
            setColumnValue(columnNode.id, getColumnInitialValue(columnNode))
        } else {
            setColumnValue(columnNode.id, undefined)
        }
    }

    useEffect(() => {
        if (columnsData && columnsData?.allColumns?.edges?.length > 0 && filterValues?.incidentId) {
            const incidentColumn = columnsData.allColumns.edges.find(column => column.node.contentType?.model === "incident")
            if (incidentColumn) {
                setColumnValue(incidentColumn.node.id, filterValues?.incidentId)
            }
        }
    }, [columnsData, filterValues?.incidentId])

    useEffect(() => {
        if (columnsData?.allColumns?.edges?.length > 0 && filterValues?.driverId) {
            const driverColumn = columnsData.allColumns.edges.find(column => column.node.contentType?.model === "driver")
            if (driverColumn) {
                setColumnValue(driverColumn.node.id, filterValues.driverId)
            }
        }
    }, [columnsData, filterValues?.driverId])

    useEffect(() => {
        const requiredBooleanColumns = columnsData?.allColumns?.edges?.filter(
            ({ node }) => node.columnType === "BOOLEAN" && node.isRequired
        ) || []

        if (requiredBooleanColumns.length === 0) return

        setColumnValues((currentValues) => {
            const nextValues = { ...currentValues }
            let changed = false

            requiredBooleanColumns.forEach(({ node }) => {
                if (nextValues[node.id] === undefined) {
                    nextValues[node.id] = false
                    changed = true
                }
            })

            return changed ? nextValues : currentValues
        })
    }, [columnsData])

    const createTask = (e) => {
        e.preventDefault()
        try {
            if (!props.apolloClient) {
                throw new Error("GQL Client not found")
            }
            if (!boardId) {
                setError("Board is required")
                return
            }
            if (!props.apolloClient) {
                throw new Error("GQL Client not found")
            }
            if (!title || title.length < 4) {
                setError("Title is required or too short")
                return
            }

            setLoading(true)

            let requiredColumnValues = columnsData?.allColumns?.edges?.filter(
                column => column.node.isRequired && column.node.columnType !== "MIRROR"
            ) || []

            // Validate required fields with explicit null/undefined/empty check. because false and 0 are valid values for booleans and numbers. Using !value would incorrectly reject these valid values.
            for (const column of requiredColumnValues) {
                if (columnValues[column.node.id] === undefined || columnValues[column.node.id] === null || columnValues[column.node.id] === "") {
                    setError(`${column.node.title} is required`)
                    setLoading(false)
                    return
                }
            }

            let columnValuesInput = Object.entries(columnValues).map(([columnId, value]) => ({
                columnId: columnId,
                // Convert all values to string because GraphQL schema expects value field as String type.
                // Backend stores all column values as strings regardless of column type.
                value: String(value),
            }))
            const input = {
                title: title,
                description: description,
                groupId: groupId || null,
                assignedToIds: repIds,
                dueDate: dueDate || null,
                boardId: boardId,
                columnValues: columnValuesInput
            }

            props.apolloClient.mutate({
                mutation: CreateTaskMutation,
                refetchQueries: ["AllTasks"],
                variables: { input },
            }).then((result) => {
                setLoading(false)
                if (!result) {
                    throw new Error("Error creating task")
                }
                if (result.data && result.data.createTask && result.data.createTask.ok) {
                    setError("")
                    closeModal("")
                } else {
                    const errorMessage =
                        result?.data?.createTask?.errors?.[0]?.messages?.[0] || "Unknown Error"
                    throw new Error(errorMessage)
                }
            }).catch((error) => {
                setError("Error creating task " + error.toString())
                setLoading(false)
            })
        } catch (error) {
            console.log("Error creating task ", error)
            setError("Error creating task " + error.toString())
            setLoading(false)
        }
    }

    return (
        <Modal isOpen={isOpen} toggle={closeModal} className="AddCustomColumnModal" centered>
            <ModalHeader>
                <span className="add-custom-column-modal-title"><i className="fa fa-check-square-o" aria-hidden="true" /> Create Task</span>
            </ModalHeader>
            <ModalBody>
                <Form onSubmit={createTask} className="addCustomColumnForm">
                    <div className="add-custom-column-intro">
                        Add a new task to the selected board. Fill in the basics first, then any custom fields required for that board.
                    </div>
                    <Row>
                        {error && <Col xs={12}><Alert color="danger">{error}</Alert></Col>}
                        <Col xs={12}><FormGroup className="add-custom-column-form-group">
                            <Label for="repIds">Board *</Label>
                            <BoardTypeAhead defaultValue={parentBoard} onChange={setBoardId} isDisabled={!!parentBoard} />
                        </FormGroup>
                        </Col>
                        <Col xs={12}>
                            <FormGroup className="add-custom-column-form-group">
                                <Label for="title">Title *</Label>
                                <Input type="string" name="title" id="title" placeholder="What needs to be done" value={title} onChange={(e) => setTitle(e.target.value)} />
                            </FormGroup>
                        </Col>
                        <Col xs={12}>
                            <FormGroup className="add-custom-column-form-group">
                                <Label for="description">Description</Label>
                                <Input type="textarea" name="description" id="description" placeholder="Add more details" value={description} onChange={(e) => setDescription(e.target.value)} rows="4" />
                            </FormGroup>
                        </Col>
                        <Col xs={12}><FormGroup className="add-custom-column-form-group">
                            <Label for="repIds">Assigned To</Label>
                            <RepsTypeAhead
                                key={boardId || "no-board-selected"}
                                onChange={(selectedRepIds) => { setRepIds(selectedRepIds) }}
                                defaultValue={repIds}
                                group={[]}
                                boardId={boardId || null}
                            />
                        </FormGroup>
                        </Col>
                        <Col xs={12}>
                            <FormGroup className="add-custom-column-form-group">
                                <Label for="groupId">Group</Label>
                                <GroupsTypeAhead
                                    isMulti={false}
                                    defaultValue={groupId}
                                    placeholder="Select Group"
                                    onChange={setGroupId}
                                />
                            </FormGroup>
                        </Col>
                        <Col xs={12}>
                            <FormGroup className="add-custom-column-form-group">
                                <Label for="dueDate">Due Date</Label>
                                <Input type="date" name="dueDate" id="dueDate" placeholder="Due Date" min={new Date().toISOString().split("T")[0]} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                            </FormGroup>
                        </Col>
                        {columnsData && columnsData.allColumns && columnsData.allColumns.edges
                            .filter((column) => column.node.columnType !== "MIRROR")
                            .map((column, index) => (
                                <Col xs={12} key={index}>
                                    <FormGroup className="add-custom-column-form-group">
                                        <FormGroup check style={{ marginBottom: "8px" }}>
                                            <Input
                                                type="checkbox"
                                                id={`${column.node.id}-enabled`}
                                                checked={isColumnEnabled(column.node)}
                                                disabled={column.node.isRequired}
                                                onChange={(e) => toggleColumnEnabled(column.node, e.target.checked)}
                                            />
                                            <Label check for={`${column.node.id}-enabled`}>
                                                {column.node.title} {column.node.isRequired ? "*" : ""}
                                            </Label>
                                        </FormGroup>
                                        {!isColumnEnabled(column.node) && (
                                            <div className="text-muted small mt-1">Enable this field to set a value.</div>
                                        )}
                                        {isColumnEnabled(column.node) && column.node.columnType === "FK_OBJECT" ? (
                                            <>
                                                <ForeignKeyInput
                                                    model={column.node.contentType.model}
                                                    onChange={(value) => setColumnValue(column.node.id, value)}
                                                    defaultValue={getColumnValue(column.node.id)}
                                                    isDisabled={
                                                        !isColumnEnabled(column.node) ||
                                                        (column.node.contentType.model === "incident" && !!lockedIncidentId) ||
                                                        (column.node.contentType.model === "driver" && !!lockedDriverId)
                                                    }
                                                />
                                            </>
                                        ) : isColumnEnabled(column.node) && column.node.columnType === "ENUM" ? (
                                            <Select
                                                options={(column.node.allowedOptions || []).map(opt => ({ value: opt, label: opt }))}
                                                onChange={(selected) => setColumnValue(column.node.id, selected ? selected.value : null)}
                                                isClearable
                                                isDisabled={!isColumnEnabled(column.node)}
                                            />
                                        ) : isColumnEnabled(column.node) && column.node.columnType === "NUMBER" ? (
                                            <Input type="number" name={column.node.id} id={column.node.id} placeholder={column.node.title} value={getColumnValue(column.node.id)} disabled={!isColumnEnabled(column.node)} onChange={(e) => setColumnValue(column.node.id, e.target.value)} />
                                        ) : isColumnEnabled(column.node) && column.node.columnType === "DECIMAL" ? (
                                            <Input type="number" name={column.node.id} id={column.node.id} placeholder={column.node.title} value={getColumnValue(column.node.id)} disabled={!isColumnEnabled(column.node)} onChange={(e) => setColumnValue(column.node.id, e.target.value)} />
                                        ) : isColumnEnabled(column.node) && column.node.columnType === "BOOLEAN" ? (
                                            <div>
                                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                    <span>Yes</span>
                                                    <div className="toggle-switch">
                                                        <input
                                                            type="checkbox"
                                                            className="toggle-switch-checkbox"
                                                            name={column.node.id}
                                                            id={column.node.id}
                                                            checked={columnValues[column.node.id] !== true}
                                                            onChange={() => setColumnValue(column.node.id, columnValues[column.node.id] === true ? false : true)}
                                                        />
                                                        <label className="toggle-switch-label" htmlFor={column.node.id}>
                                                            <span className="toggle-switch-inner" />
                                                            <span className="toggle-switch-switch" />
                                                        </label>
                                                    </div>
                                                    <span>No</span>
                                                </div>
                                            </div>
                                        ) : isColumnEnabled(column.node) && column.node.columnType === "DATE" ? (
                                            <Input type="date" name={column.node.id} id={column.node.id} placeholder={column.node.title} value={getColumnValue(column.node.id)} disabled={!isColumnEnabled(column.node)} onChange={(e) => setColumnValue(column.node.id, e.target.value)} />
                                        ) : isColumnEnabled(column.node) && column.node.columnType === "TIME" ? (
                                            <Input type="time" name={column.node.id} id={column.node.id} placeholder={column.node.title} value={getColumnValue(column.node.id)} disabled={!isColumnEnabled(column.node)} onChange={(e) => setColumnValue(column.node.id, e.target.value)} />
                                        ) : isColumnEnabled(column.node) && column.node.columnType === "DATETIME" ? (
                                            <Input type="datetime-local" name={column.node.id} id={column.node.id} placeholder={column.node.title} value={getColumnValue(column.node.id)} disabled={!isColumnEnabled(column.node)} onChange={(e) => setColumnValue(column.node.id, e.target.value)} />
                                        ) : isColumnEnabled(column.node) ? (
                                            <Input type="string" name={column.node.id} id={column.node.id} placeholder={column.node.title} value={getColumnValue(column.node.id)} disabled={!isColumnEnabled(column.node)} onChange={(e) => setColumnValue(column.node.id, e.target.value)} />
                                        ) : null}
                                    </FormGroup>
                                </Col>
                            ))}

                    </Row>
                    <ModalFooter className="add-custom-column-modal-footer">
                        {loading ?
                            <Button color="primary" disabled={true}>Creating Task...</Button> :
                            <Button color="primary" type="submit" disabled={
                                !title ||
                                !boardId ||
                                loading
                            }>Create Task</Button>}
                        <Button color="secondary" type="button" onClick={closeModal}>Close</Button>
                    </ModalFooter>
                </Form>
            </ModalBody>
        </Modal>
    )
}

export default (withApolloClient(NewTaskModal))
